/**
 * @fileoverview Tools Configuration
 * Master configuration for organizational structure, permissions, and tools
 * This file centralizes everything needed for permission-based menu generation
 */

// =============================================================================
// MASTER TOOLS CONFIGURATION
// =============================================================================

export const TOOLS_CONFIG = {
  divisions: {
    energy_infrastructure: {
      id: 'energy_infrastructure',
      name: 'Energy & Infrastructure Services',
      description: 'Energy and infrastructure products and services',
      icon: 'mdi-flash',
      groups: {
        business_operations: {
          id: 'business_operations',
          name: 'Business Operations',
          description: 'Business operations and management',
          icon: 'mdi-briefcase',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        market_initiatives: {
          id: 'market_initiatives',
          name: 'Market Initiatives & Partnerships',
          description: 'Market development and strategic partnerships',
          icon: 'mdi-handshake',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        infrastructure_products: {
          id: 'infrastructure_products',
          name: 'Infrastructure Products & Services',
          description: 'Infrastructure products and service delivery',
          icon: 'mdi-factory',
          tools: [
            // No tools currently - ready for future additions
          ]
        }
      }
    },

    corporate_planning: {
      id: 'corporate_planning',
      name: 'Corporate Planning, Risk, Advisory Services',
      description: 'Corporate planning, risk management, and advisory services',
      icon: 'mdi-chart-line',
      groups: {
        corporate_planning: {
          id: 'corporate_planning',
          name: 'Corporate Planning, Risk, Advisory Services',
          description: 'Strategic planning and risk advisory',
          icon: 'mdi-strategy',
          tools: [
            // No tools currently - ready for future additions
          ]
        }
      }
    },

    finance: {
      id: 'finance',
      name: 'Finance',
      description: 'Financial management and regulatory affairs',
      icon: 'mdi-currency-usd',
      groups: {
        regulatory_affairs: {
          id: 'regulatory_affairs',
          name: 'Regulatory Affairs',
          description: 'Regulatory compliance and affairs management',
          icon: 'mdi-gavel',
          tools: [
            {
              id: 'FinanceRegulatoryRateApplication',
              name: 'Rate Application',
              description: 'Rate application management and analysis',
              route: '/finance/regulatory/rate-application',
              icon: 'mdi-file-document',
              status: 'active',
              features: ['Document Analysis', 'AI Assistant', 'Regulatory Compliance']
            }
          ]
        },
        strategic_sourcing: {
          id: 'strategic_sourcing',
          name: 'Strategic Sourcing & Logistics',
          description: 'Strategic sourcing and logistics management',
          icon: 'mdi-truck',
          tools: [
            // No tools currently - ready for future additions
          ]
        }
      }
    },

    operations: {
      id: 'operations',
      name: 'Operations, Distribution, Generation',
      description: 'Operations, distribution systems, and power generation',
      icon: 'mdi-transmission-tower',
      groups: {
        system_operations: {
          id: 'system_operations',
          name: 'System Operations & Grid Automation',
          description: 'System operations and grid automation management',
          icon: 'mdi-grid',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        distribution_construction: {
          id: 'distribution_construction',
          name: 'Distribution Construction & Maintenance',
          description: 'Distribution infrastructure construction and maintenance',
          icon: 'mdi-hammer-wrench',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        generation_development: {
          id: 'generation_development',
          name: 'Generation: Development & Operations',
          description: 'Power generation development and operations',
          icon: 'mdi-lightning-bolt',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        distribution_engineering: {
          id: 'distribution_engineering',
          // name: 'Distribution Engineering & Asset Management',
          name: 'Distribution Engineering',
          description: 'Distribution engineering and asset management',
          icon: 'mdi-transmission-tower',
          tools: [
            {
              id: 'OperationsDistributionSwitchOrders',
              name: 'Switch Orders',
              description: 'Switch order management and automation',
              route: '/operations/distribution/switch-orders',
              icon: 'mdi-electric-switch',
              status: 'active',
              features: ['Order Management', 'Automation', 'Analytics']
            }
          ]
        },
        distribution_program: {
          id: 'distribution_program',
          name: 'Distribution Program Delivery',
          description: 'Distribution program planning and delivery',
          icon: 'mdi-clipboard-list',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        generation_energy: {
          id: 'generation_energy',
          name: 'Generation: Energy & Operations',
          description: 'Energy generation and operational management',
          icon: 'mdi-meter-electric',
          tools: [
            // No tools currently - ready for future additions
          ]
        }
      }
    },

    customer: {
      id: 'customer',
      name: 'Customer',
      description: 'Customer service, communications, and energy transition',
      icon: 'mdi-account-group',
      groups: {
        customer_service: {
          id: 'customer_service',
          name: 'Customer Service',
          description: 'Customer support and service delivery',
          icon: 'mdi-headset',
          tools: [
            {
              id: 'CustomerServiceCallAnalysis',
              name: 'Call Analysis',
              description: 'AI-powered call analysis and insights',
              route: '/customer/service/call-analysis',
              icon: 'mdi-phone-settings',
              status: 'active',
              features: ['AI Analysis', 'Sentiment Analysis', 'Call Categorization']
            },
            {
              id: 'CustomerServiceSmartSearch',
              name: 'Smart Search',
              description: 'Intelligent web search capabilities',
              route: '/customer/service/smart-search',
              icon: 'mdi-magnify',
              status: 'active',
              features: ['Web Search', 'AI Assistance']
            }
          ]
        },
        communications: {
          id: 'communications',
          name: 'Communications & Public Affairs',
          description: 'Communications and public relations management',
          icon: 'mdi-bullhorn',
          tools: [
            {
              id: 'CustomerCommunicationsContentManager',
              name: 'Content Manager',
              description: 'Content management and creation tools',
              route: '/customer/communications/content-manager',
              icon: 'mdi-file-edit',
              status: 'active',
              features: ['Content Creation', 'Document Management', 'Collaboration']
            }
          ]
        },
        energy_transition: {
          id: 'energy_transition',
          name: 'Energy Transition Customer Strategy & Innovation',
          description: 'Energy transition strategy and customer innovation',
          icon: 'mdi-leaf',
          tools: [
            // No tools currently - ready for future additions
          ]
        }
      }
    },

    people_culture: {
      id: 'people_culture',
      name: 'People and Culture',
      description: 'Human resources, organizational development, and workplace culture',
      icon: 'mdi-account-heart',
      groups: {
        people_systems: {
          id: 'people_systems',
          name: 'People Systems, Technology & Analytics',
          description: 'HR systems, technology, and people analytics',
          icon: 'mdi-chart-box',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        change_organization: {
          id: 'change_organization',
          name: 'Change and Organization Development',
          description: 'Organizational change and development initiatives',
          icon: 'mdi-trending-up',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        hr_service: {
          id: 'hr_service',
          name: 'Human Resources Service & Labour Relations',
          description: 'HR services and labour relations management',
          icon: 'mdi-account-tie',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        health_safety: {
          id: 'health_safety',
          name: 'Health, Safety, Environment',
          description: 'Health, safety, and environmental management',
          icon: 'mdi-shield-check',
          tools: [
            // No tools currently - ready for future additions
          ]
        }
      }
    },

    information_technology: {
      id: 'information_technology',
      name: 'Information & Technology',
      description: 'Information technology and digital infrastructure',
      icon: 'mdi-monitor',
      groups: {
        enterprise_applications: {
          id: 'enterprise_applications',
          name: 'Enterprise Applications',
          description: 'Enterprise application development and management',
          icon: 'mdi-application',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        cybersecurity: {
          id: 'cybersecurity',
          name: 'Cybersecurity & IT Infrastructure',
          description: 'Cybersecurity and IT infrastructure management',
          icon: 'mdi-security',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        enterprise_architecture: {
          id: 'enterprise_architecture',
          name: 'Enterprise Architecture',
          description: 'Enterprise architecture and system design',
          icon: 'mdi-sitemap',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        it_planning: {
          id: 'it_planning',
          name: 'IT Planning, Programs & Grid Technology',
          description: 'IT planning, programs, and grid technology management',
          icon: 'mdi-calendar-check',
          tools: [
            // No tools currently - ready for future additions
          ]
        },
        skunkworks: {
          id: 'skunkworks',
          name: 'Skunkworks',
          description: 'Pilot projects and experimental technology tools',
          icon: 'mdi-flask',
          tools: [
            {
              id: 'SkunkworksAgentAssist',
              name: 'Agent Assist',
              description: 'AI-powered agent assistance tool',
              route: '/skunkworks/agent-assist',
              icon: 'mdi-robot',
              status: 'experimental',
              features: ['AI Assistance', 'Knowledge Base', 'Real-time Support']
            },
            {
              id: 'SkunkworksArtificialAgent',
              name: 'Artificial Agent',
              description: 'Automated agent for customer interactions',
              route: '/skunkworks/artificial-agent',
              icon: 'mdi-android',
              status: 'experimental',
              features: ['Automation', 'AI Conversations', 'Customer Service']
            },
            {
              id: 'SkunkworksBillPrediction',
              name: 'Bill Prediction',
              description: 'AI-powered bill prediction and analysis',
              route: '/skunkworks/bill-prediction',
              icon: 'mdi-crystal-ball',
              status: 'experimental',
              features: ['Predictive Analytics', 'Bill Analysis', 'Customer Insights']
            },
            {
              id: 'SkunkworksChatAssistant',
              name: 'Chat Assistant',
              description: 'Google Chat integration and assistance',
              route: '/skunkworks/chat-assistant',
              icon: 'mdi-chat',
              status: 'experimental',
              features: ['Chat Integration', 'AI Assistant', 'Workflow Automation']
            },
            {
              id: 'SkunkworksDamageAssessment',
              name: 'Damage Assessment',
              description: 'AI-powered damage assessment tools',
              route: '/skunkworks/damage-assessment',
              icon: 'mdi-camera-iris',
              status: 'experimental',
              features: ['Image Analysis', 'Damage Detection', 'Assessment Reports']
            },
            {
              id: 'SkunkworksForestry',
              name: 'Forestry',
              description: 'Forestry management and analysis tools',
              route: '/skunkworks/forestry',
              icon: 'mdi-tree',
              status: 'experimental',
              features: ['Vegetation Management', 'Risk Assessment', 'Planning Tools']
            },
            {
              id: 'SkunkworksImagen',
              name: 'Imagen',
              description: 'AI image generation and editing',
              route: '/skunkworks/imagen',
              icon: 'mdi-image-multiple',
              status: 'experimental',
              features: ['Image Generation', 'AI Art', 'Visual Content']
            },
            {
              id: 'SkunkworksMusic',
              name: 'Music',
              description: 'AI music generation and audio tools',
              route: '/skunkworks/music',
              icon: 'mdi-music',
              status: 'experimental',
              features: ['Audio Generation', 'Music Creation', 'Sound Design']
            },
            {
              id: 'SkunkworksNameplateAutomation',
              name: 'Nameplate Automation',
              description: 'Automated nameplate recognition and processing',
              route: '/skunkworks/nameplate-automation',
              icon: 'mdi-tag-text',
              status: 'experimental',
              features: ['OCR', 'Text Recognition', 'Data Extraction']
            },
            {
              id: 'SkunkworksPodcasts',
              name: 'Podcasts',
              description: 'AI-powered podcast generation and editing',
              route: '/skunkworks/podcasts',
              icon: 'mdi-microphone',
              status: 'experimental',
              features: ['Audio Generation', 'Voice Synthesis', 'Content Creation']
            },
            {
              id: 'SkunkworksWorkforceAutomation',
              name: 'Workforce Automation',
              description: 'Workforce management and automation tools',
              route: '/skunkworks/workforce-automation',
              icon: 'mdi-account-cog',
              status: 'experimental',
              features: ['Process Automation', 'Workflow Management', 'HR Analytics']
            }
          ]
        }
      }
    },

    general_counsel: {
      id: 'general_counsel',
      name: 'General Counsel',
      description: 'Legal affairs and counsel services',
      icon: 'mdi-scale-balance',
      groups: {
        general_counsel: {
          id: 'general_counsel',
          name: 'General Counsel',
          description: 'Legal services and counsel',
          icon: 'mdi-gavel',
          tools: [
            // No tools currently - ready for future additions
          ]
        }
      }
    }
  }
}

// =============================================================================
// USER ROLES CONFIGURATION
// =============================================================================

export const ROLES_CONFIG = {
  hierarchy: ['', 'Employee', 'Pioneer', 'Manager', 'Admin'], // Lowest to highest privilege (empty = inactive)
  roles: [
    {
      id: '',
      name: 'Inactive',
      description: 'Inactive user with no access (no role assigned)',
      level: 0,
      permissions: [],
      canInviteUsers: false,
      canManageUsers: false
    },
    {
      id: 'Employee',
      name: 'Employee',
      description: 'Standard employee access without management tools',
      level: 1,
      permissions: ['basic_access'],
      canInviteUsers: false,
      canManageUsers: false
    },
    {
      id: 'Pioneer',
      name: 'Pioneer',
      description: 'Enhanced permissions with access to experimental features',
      level: 2,
      permissions: ['basic_access', 'experimental_access', 'beta_features'],
      canInviteUsers: false,
      canManageUsers: false
    },
    {
      id: 'Manager',
      name: 'Manager',
      description: 'Management access with user management for assigned groups',
      level: 3,
      permissions: ['basic_access', 'experimental_access', 'team_management', 'group_user_management'],
      canInviteUsers: true,
      canManageUsers: true, // For their groups only
      maxRoleCanAssign: 'Pioneer' // Managers can only assign up to Pioneer
    },
    {
      id: 'Admin',
      name: 'Administrator',
      description: 'Full system administrative access',
      level: 4,
      permissions: ['full_access', 'user_management', 'system_admin', 'basic_access', 'experimental_access', 'team_management', 'group_user_management'],
      canInviteUsers: true,
      canManageUsers: true, // For all users
      maxRoleCanAssign: 'Admin' // Admins can assign any role including Admin
    }
  ]
}

// =============================================================================
// PERMISSION CHECKING UTILITIES
// =============================================================================

/**
 * Get role level for hierarchy comparison
 */
export function getRoleLevel(roleId) {
  const role = ROLES_CONFIG.roles.find(r => r.id === roleId)
  return role ? role.level : -1
}

/**
 * Get user's role level (simplified single role)
 */
export function getUserRoleLevel(user) {
  if (!user?.role && user?.role !== '') return -1 // Handle empty string as valid (inactive)
  return getRoleLevel(user.role)
}

/**
 * Check if user has at least the required role level
 */
export function userHasRoleLevel(user, requiredRoleId) {
  const userLevel = getUserRoleLevel(user)
  const requiredLevel = getRoleLevel(requiredRoleId)
  return userLevel >= requiredLevel
}

/**
 * Get user's role configuration object
 */
export function getUserRoleConfig(user) {
  if (!user?.role && user?.role !== '') return null
  return ROLES_CONFIG.roles.find(r => r.id === user.role)
}

/**
 * Check if user can invite other users
 */
export function userCanInviteUsers(user) {
  const roleConfig = getUserRoleConfig(user)
  return roleConfig?.canInviteUsers || false
}

/**
 * Check if user can manage other users (in their groups)
 */
export function userCanManageUsers(user) {
  const roleConfig = getUserRoleConfig(user)
  return roleConfig?.canManageUsers || false
}

/**
 * Get the maximum role a user can assign to others
 */
export function getUserMaxAssignableRole(user) {
  const roleConfig = getUserRoleConfig(user)
  return roleConfig?.maxRoleCanAssign || null
}

/**
 * Check if a user has access to a specific tool
 */
export function userHasToolAccess(user, toolId) {
  console.log(`🔍 Checking access: ${user?.email} → ${toolId}`)
  
  if (!user || !toolId) {
    console.log(`❌ Missing user or toolId`)
    return false
  }
  
  // Inactive users (no role) have no access
  if (getUserRoleLevel(user) < getRoleLevel('Employee')) {
    console.log(`❌ User role level too low: ${user.role}`)
    return false
  }
  
  // Admin always has access to everything
  if (user.role === 'Admin') {
    console.log(`✅ Admin access granted`)
    return true
  }
  
  // Get the tool and find which group it belongs to
  const tool = getToolById(toolId)
  if (!tool) {
    console.log(`❌ Tool not found: ${toolId}`)
    return false
  }
  
  // Find which group this tool belongs to
  let toolGroupId = null
  Object.values(TOOLS_CONFIG.divisions).forEach(division => {
    Object.values(division.groups).forEach(group => {
      if (group.tools.some(t => t.id === toolId)) {
        toolGroupId = group.id
      }
    })
  })
  
  if (!toolGroupId) {
    console.log(`❌ Could not find group for tool: ${toolId}`)
    return false
  }
  
  console.log(`📍 Tool ${toolId} belongs to group: ${toolGroupId}`)
  
  // Check if user is in the group where this tool belongs
  const userGroups = user.groups || []
  console.log(`👥 User groups:`, userGroups)
  
  if (!userGroups.includes(toolGroupId)) {
    console.log(`❌ User not in required group: ${toolGroupId}`)
    return false
  }
  
  console.log(`✅ User is in group: ${toolGroupId}`)
  
  // User is in the group - now check access:
  
  // 1. Manager of the group gets access to all group tools
  if (user.role === 'Manager') {
    console.log(`✅ Manager access granted for group: ${toolGroupId}`)
    return true
  }
  
  // 2. Check explicit tool permissions
  const toolPermission = user.tool_permissions?.[toolId]
  console.log(`🔐 Tool permission for ${toolId}:`, toolPermission)
  
  if (toolPermission?.access === true) {
    console.log(`✅ Explicit tool permission granted`)
    return true
  }
  
  // No access granted
  console.log(`❌ No access granted - no explicit permission`)
  return false
}

/**
 * Check if a user can manage a specific group
 */
export function userCanManageGroup(user, groupId) {
  if (!user || !groupId) return false
  
  // Admin can manage everything
  if (user.roles?.includes('Admin')) return true
  
  // Check group manager status
  return user.manager_status?.[groupId] === true
}

/**
 * Get all tools a user has access to
 */
export function getUserAccessibleTools(user) {
  const accessibleTools = []
  
  Object.values(TOOLS_CONFIG.divisions).forEach(division => {
    Object.values(division.groups).forEach(group => {
      group.tools.forEach(tool => {
        if (userHasToolAccess(user, tool.id)) {
          accessibleTools.push({
            ...tool,
            groupId: group.id,
            groupName: group.name,
            divisionId: division.id,
            divisionName: division.name
          })
        }
      })
    })
  })
  
  return accessibleTools
}

/**
 * Generate menu structure based on user permissions
 */
export function generateUserMenu(user) {
  console.log('🗂️ Generating user menu for:', user?.email, {
    role: user?.role,
    groups: user?.groups,
    tool_permissions_count: Object.keys(user?.tool_permissions || {}).length
  })
  
  const menu = []
  
  Object.values(TOOLS_CONFIG.divisions).forEach(division => {
    console.log(`📁 Checking division: ${division.name}`)
    
    const divisionMenu = {
      id: division.id,
      name: division.name,
      icon: division.icon,
      groups: []
    }
    
    Object.values(division.groups).forEach(group => {
      console.log(`  📂 Checking group: ${group.name} (${group.id})`)
      
      const accessibleTools = group.tools.filter(tool => {
        const hasAccess = userHasToolAccess(user, tool.id)
        console.log(`    🔧 Tool: ${tool.name} → ${hasAccess ? '✅ ACCESS' : '❌ NO ACCESS'}`)
        return hasAccess
      })
      
      if (accessibleTools.length > 0) {
        console.log(`  ✅ Group ${group.name} added with ${accessibleTools.length} tools`)
        divisionMenu.groups.push({
          id: group.id,
          name: group.name,
          icon: group.icon,
          tools: accessibleTools
        })
      } else {
        console.log(`  ❌ Group ${group.name} skipped - no accessible tools`)
      }
    })
    
    if (divisionMenu.groups.length > 0) {
      console.log(`✅ Division ${division.name} added with ${divisionMenu.groups.length} groups`)
      menu.push(divisionMenu)
    } else {
      console.log(`❌ Division ${division.name} skipped - no accessible groups`)
    }
  })
  
  console.log('🏁 Final menu:', menu.length, 'divisions')
  return menu
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all divisions
 */
export function getDivisions() {
  return Object.values(TOOLS_CONFIG.divisions)
}

/**
 * Get groups for a specific division
 */
export function getGroupsForDivision(divisionId) {
  const division = TOOLS_CONFIG.divisions[divisionId]
  return division ? Object.values(division.groups) : []
}

/**
 * Get all groups across all divisions
 */
export function getAllGroups() {
  const allGroups = []
  Object.values(TOOLS_CONFIG.divisions).forEach(division => {
    Object.values(division.groups).forEach(group => {
      allGroups.push({
        ...group,
        divisionId: division.id,
        divisionName: division.name
      })
    })
  })
  return allGroups
}

/**
 * Get tools for a specific group
 */
export function getToolsForGroup(groupId) {
  for (const division of Object.values(TOOLS_CONFIG.divisions)) {
    const group = division.groups[groupId]
    if (group) {
      return group.tools
    }
  }
  return []
}

/**
 * Get all tools across all groups
 */
export function getAllTools() {
  const allTools = []
  Object.values(TOOLS_CONFIG.divisions).forEach(division => {
    Object.values(division.groups).forEach(group => {
      group.tools.forEach(tool => {
        allTools.push({
          ...tool,
          groupId: group.id,
          groupName: group.name,
          divisionId: division.id,
          divisionName: division.name
        })
      })
    })
  })
  return allTools
}

/**
 * Find a tool by its ID
 */
export function getToolById(toolId) {
  for (const division of Object.values(TOOLS_CONFIG.divisions)) {
    for (const group of Object.values(division.groups)) {
      const tool = group.tools.find(t => t.id === toolId)
      if (tool) {
        return {
          ...tool,
          groupId: group.id,
          groupName: group.name,
          divisionId: division.id,
          divisionName: division.name
        }
      }
    }
  }
  return null
}

/**
 * Get division name by ID
 */
export function getDivisionName(divisionId) {
  const division = TOOLS_CONFIG.divisions[divisionId]
  return division ? division.name : divisionId
}

/**
 * Get group name by ID
 */
export function getGroupName(groupId) {
  for (const division of Object.values(TOOLS_CONFIG.divisions)) {
    const group = division.groups[groupId]
    if (group) {
      return group.name
    }
  }
  return groupId
}

// =============================================================================
// LEGACY COMPATIBILITY
// =============================================================================

// Legacy exports for backward compatibility
export const ORGANIZATION_CONFIG = { divisions: TOOLS_CONFIG.divisions }
export const AVAILABLE_TOOLS = TOOLS_CONFIG.divisions
export const AVAILABLE_ROLES = ROLES_CONFIG.roles.map(role => ({ id: role.id, name: role.name }))
export const ROLE_IDS = ROLES_CONFIG.roles.map(role => role.id)
export const DIVISIONS_AND_GROUPS = TOOLS_CONFIG.divisions 